import React, { useCallback, useMemo } from 'react';

import LibraryIcon from '@atlaskit/icon/core/library';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { useRovoNavigation } from '@atlassian/conversation-assistant/controllers/rovo-navigation';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useIntl } from 'react-intl';

import { type CommonSideNavItemProps } from '@townsquare/griffin-navigation/types';
import { useRouterActions } from '@townsquare/router/primitives/actions';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { matchRoute } from '@townsquare/router/primitives/match-route';
import { generatePath } from '@townsquare/router/primitives/path';
import { useRouter } from '@townsquare/router/useRouter';
import { chatRoute, createChatRouteQuery } from '@townsquare/routes/chat';
import { useAssistanceServiceParams } from '@townsquare/rovo/hooks';
import { appendSearchStringToPath } from '@townsquare/search-params';

export const RovoLibraryNavItem = (props: CommonSideNavItemProps): React.JSX.Element => {
	const { onClick, item } = props;
	const intl = useIntl();
	const { pushTo } = useRouterActions();
	const [routerStore] = useRouter();
	const { activeMenu } = useUIStore();
	const routeDependencies = useRouteDependencies();

	const assistanceServiceParams = useAssistanceServiceParams({
		experienceId: 'ai-mate',
		touchpointSource: 'townsquare-sidenav',
	});
	const { handleLibraryClick } = useRovoNavigation({
		assistanceServiceParams,
	});

	const isOnChatRoute = !!matchRoute([chatRoute], routerStore.location.pathname, undefined);
	const isSelected = isOnChatRoute && activeMenu === 'library';

	const libraryQuery = useMemo(
		() =>
			createChatRouteQuery(routerStore.query, {
				rovoChatPathway: 'library',
			}),
		[routerStore.query],
	);
	// eslint-disable-next-line no-restricted-syntax
	const librarySearchParams = new URLSearchParams(libraryQuery);
	const libraryHref = appendSearchStringToPath(
		generatePath(chatRoute, routeDependencies),
		librarySearchParams.toString(),
	);

	const onLibraryClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			pushTo(chatRoute, { query: libraryQuery });
			// Trigger the library panel to open
			handleLibraryClick();
			onClick(item, e);
		},
		[libraryQuery, pushTo, handleLibraryClick, item, onClick],
	);

	return (
		<LinkMenuItem
			href={libraryHref}
			elemBefore={<LibraryIcon label="" color="currentColor" />}
			onClick={onLibraryClick}
			isSelected={isSelected}
			testId="side-nav-library"
		>
			{intl.formatMessage({
				id: 'townsquare.atlassian-home.side-nav.library',
				description: 'Side nav button to open the Library page',
				defaultMessage: 'Library',
			})}
		</LinkMenuItem>
	);
};
